import { useId, useState } from 'react';
import { Controller, useFieldArray, useWatch, type UseFormReturn } from 'react-hook-form';
import {
  CONTRIBUINTES_ICMS,
  ROTULO_CONTRIBUINTE_ICMS,
  ROTULO_PAPEL_PARCEIRO,
  ROTULO_TIPO_ENDERECO,
  TIPOS_ENDERECO,
  documentoValido,
  soDigitos,
  type ContribuinteIcms,
  type NaturezaParceiro,
  type ParceiroResumoResponse,
  type PapelParceiro,
  type TipoEndereco,
} from '@triade/contracts';
import { useParceiroPorDocumento } from '@/api/parceiros';
import { TITULO_CAMPO_PROTHEUS } from '@/components/integracoes-erp/selo-protheus';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { buscarCep, validarMunicipio, type ResultadoValidacaoMunicipio } from '@/lib/cep';
import { mascaraCep, mascaraCpfCnpj, mascaraTelefone } from '@/lib/mascaras';

/** Limites do cadastro (mesmos do contrato). */
export const LIMITE_ENDERECOS = 10;
export const LIMITE_CONTATOS = 20;

const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'] as const;

const CLASSE_CAMPO =
  'flex h-10 w-full min-w-0 rounded-md border border-input bg-card px-3 text-body text-ink outline-none focus-visible:border-primary focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50';

/** Um endereco na tela: strings vazias = nao informado (quem salva converte para ausente). */
export interface EnderecoCampo {
  tipo: TipoEndereco;
  principal: boolean;
  /** So digitos (8). */
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  municipio: string;
  uf: string;
  codigoIbge: string;
}

export interface ContatoCampo {
  nome: string;
  cargo: string;
  email: string;
  /** Mascarado na tela (ex.: "(11) 99999-8888"). */
  telefone: string;
}

/** Campos que o sub-formulario le e grava. A tela pai inclui estes campos no seu formulario. */
export interface ParceiroCampos {
  natureza: NaturezaParceiro;
  nome: string;
  fantasia: string;
  /** So digitos (CPF 11 ou CNPJ 14). A mascara e so de tela. */
  documento: string;
  ie: string;
  contribuinteIcms: ContribuinteIcms | '';
  observacoes: string;
  enderecos: EnderecoCampo[];
  contatos: ContatoCampo[];
}

/** Valores iniciais de um cadastro em branco. */
export function parceiroCamposIniciais(natureza: NaturezaParceiro = 'JURIDICA'): ParceiroCampos {
  return { natureza, nome: '', fantasia: '', documento: '', ie: '', contribuinteIcms: '', observacoes: '', enderecos: [], contatos: [] };
}

export interface ParceiroBlocosProps<T extends ParceiroCampos = ParceiroCampos> {
  /** Formulario da tela pai (react-hook-form). Os campos de ParceiroCampos precisam existir nele. */
  form: UseFormReturn<T>;
  /** Papel em que o cadastro esta sendo feito (mostrado no aviso de duplicidade). */
  papel: PapelParceiro;
  /** Id do parceiro em edicao: o proprio cadastro nao conta como duplicidade. */
  parceiroId?: string;
  /** Usuario escolheu usar o parceiro ja cadastrado com este CPF/CNPJ. */
  onUsarExistente: (resumo: ParceiroResumoResponse) => void;
  desabilitado?: boolean;
  /**
   * Campos do parceiro que vem do Protheus (somente leitura). Chaves de `CAMPOS_DO_PROTHEUS` (ex.: `nome`, `enderecoPrincipal`,
   * `contatoProtheus`). Sem o prop, nada e travado. `enderecoPrincipal` trava o endereco principal; `contatoProtheus` o contato "Protheus".
   */
  camposDoProtheus?: ReadonlySet<string>;
}

type Status = 'buscando' | 'falhou';

const ehIndisponivel = (r: ResultadoValidacaoMunicipio): r is { indisponivel: true } => 'indisponivel' in r;

export function ParceiroBlocos<T extends ParceiroCampos = ParceiroCampos>({
  form,
  papel,
  parceiroId,
  onUsarExistente,
  desabilitado = false,
  camposDoProtheus,
}: ParceiroBlocosProps<T>) {
  const uid = useId();
  // O sub-formulario so conhece ParceiroCampos; o tipo do formulario da tela pai nao muda o comportamento.
  const f = form as unknown as UseFormReturn<ParceiroCampos>;
  const { control, setValue, setError, clearErrors, getValues } = f;

  const natureza = useWatch({ control, name: 'natureza' });
  const documentoAtual = useWatch({ control, name: 'documento' }) ?? '';
  const enderecosAtuais = useWatch({ control, name: 'enderecos' }) ?? [];
  const contatosAtuais = useWatch({ control, name: 'contatos' }) ?? [];

  const enderecos = useFieldArray({ control, name: 'enderecos' });
  const contatos = useFieldArray({ control, name: 'contatos' });

  // Duplicidade: consulta so ao sair do campo com documento valido; so vale para o documento ainda digitado.
  const [documentoConsultado, setDocumentoConsultado] = useState<string | null>(null);
  const consulta = useParceiroPorDocumento(documentoConsultado);
  const outro = consulta.data?.parceiro ?? null;
  const existente =
    documentoConsultado !== null && documentoConsultado === documentoAtual && outro !== null && outro.id !== parceiroId ? outro : null;

  const [statusCep, setStatusCep] = useState<Record<string, Status>>({});

  function aoSairDocumento(valor: string) {
    const d = soDigitos(valor);
    clearErrors('documento');
    if (d.length === 0) {
      setDocumentoConsultado(null);
      return;
    }
    if (d.length !== 11 && d.length !== 14) {
      setError('documento', { type: 'validate', message: 'Informe 11 dígitos (CPF) ou 14 (CNPJ)' });
      setDocumentoConsultado(null);
      return;
    }
    if (!documentoValido(d)) {
      setError('documento', { type: 'validate', message: 'CPF ou CNPJ inválido (confira os dígitos)' });
      setDocumentoConsultado(null);
      return;
    }
    setDocumentoConsultado(d);
  }

  function marcarPrincipal(i: number) {
    enderecosAtuais.forEach((_, j) => setValue(`enderecos.${j}.principal` as const, j === i));
  }

  /** Aplica o resultado da conferencia de municipio no endereco i. */
  function aplicarConferencia(i: number, r: ResultadoValidacaoMunicipio) {
    if ('ok' in r && r.ok) {
      clearErrors(`enderecos.${i}.municipio` as const);
      setValue(`enderecos.${i}.codigoIbge` as const, r.codigoIbge);
    } else if (!ehIndisponivel(r)) {
      setError(`enderecos.${i}.municipio` as const, { type: 'validate', message: 'Município não encontrado nesta UF' });
      setValue(`enderecos.${i}.codigoIbge` as const, '');
    }
  }

  async function conferirMunicipio(i: number) {
    const uf = getValues(`enderecos.${i}.uf` as const);
    const municipio = getValues(`enderecos.${i}.municipio` as const);
    clearErrors(`enderecos.${i}.municipio` as const);
    if (!uf || municipio.trim() === '') return;
    aplicarConferencia(i, await validarMunicipio(uf, municipio));
  }

  async function preencherPorCep(i: number, chave: string, cep: string) {
    if (cep.length !== 8) return;
    setStatusCep((s) => ({ ...s, [chave]: 'buscando' }));
    const achado = await buscarCep(cep);
    if (getValues(`enderecos.${i}.cep` as const) !== cep) return; // o CEP mudou enquanto consultava
    if (!achado) {
      setStatusCep((s) => ({ ...s, [chave]: 'falhou' }));
      return;
    }
    setStatusCep((s) => {
      const { [chave]: _removido, ...resto } = s;
      return resto;
    });
    setValue(`enderecos.${i}.logradouro` as const, achado.logradouro);
    setValue(`enderecos.${i}.bairro` as const, achado.bairro);
    setValue(`enderecos.${i}.municipio` as const, achado.municipio);
    setValue(`enderecos.${i}.uf` as const, achado.uf);
    setValue(`enderecos.${i}.codigoIbge` as const, achado.codigoIbge);
    aplicarConferencia(i, await validarMunicipio(achado.uf, achado.municipio));
  }

  const erros = f.formState.errors;
  const errosDoc = erros.documento?.message;

  // Campos do Protheus: travados. O principal so trava se existir um (trocar o principal mexeria no endereco travado).
  const travado = (campo: string): boolean => camposDoProtheus?.has(campo) === true;
  const tituloDe = (campo: string): string | undefined => (travado(campo) ? TITULO_CAMPO_PROTHEUS : undefined);
  const principalTravado = travado('enderecoPrincipal') && enderecosAtuais.some((e) => e.principal === true);
  const enderecoTravado = (i: number): boolean => principalTravado && enderecosAtuais[i]?.principal === true;
  /** Contato "Protheus" = o de nome exatamente "Protheus" (nome travado: nao some nem muda de identidade). */
  const contatoTravado = (i: number): boolean => travado('contatoProtheus') && contatosAtuais[i]?.nome === 'Protheus';

  return (
    <div className="flex flex-col gap-6" data-slot="parceiro-blocos">
      <fieldset className="flex flex-col gap-4" disabled={desabilitado}>
        <legend className="text-sm font-semibold text-ink">Identidade</legend>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${uid}-natureza`}>Natureza</Label>
            <Controller
              control={control}
              name="natureza"
              render={({ field }) => (
                <select
                  id={`${uid}-natureza`}
                  aria-label="Natureza"
                  disabled={travado('natureza')}
                  title={tituloDe('natureza')}
                  className={CLASSE_CAMPO}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  ref={field.ref}
                >
                  <option value="JURIDICA">Pessoa jurídica (CNPJ)</option>
                  <option value="FISICA">Pessoa física (CPF)</option>
                </select>
              )}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${uid}-documento`}>CPF ou CNPJ</Label>
            <Controller
              control={control}
              name="documento"
              render={({ field }) => (
                <Input
                  id={`${uid}-documento`}
                  aria-label="CPF ou CNPJ"
                  disabled={travado('documento')}
                  title={tituloDe('documento')}
                  aria-invalid={errosDoc ? true : undefined}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder={natureza === 'FISICA' ? '000.000.000-00' : '00.000.000/0000-00'}
                  value={mascaraCpfCnpj(field.value ?? '')}
                  onChange={(e) => field.onChange(soDigitos(e.target.value).slice(0, 14))}
                  onBlur={() => {
                    field.onBlur();
                    aoSairDocumento(field.value ?? '');
                  }}
                  ref={field.ref}
                />
              )}
            />
            {errosDoc ? (
              <p role="alert" className="text-sm text-destructive">
                {errosDoc}
              </p>
            ) : null}
            {existente ? (
              <div role="status" className="rounded-md border border-input bg-card p-3 text-sm text-ink">
                Este CPF/CNPJ já está cadastrado para {existente.nome} ({existente.papeis.map((p) => ROTULO_PAPEL_PARCEIRO[p]).join(', ')}).
                {existente.papeis.includes(papel) ? ` Já é ${ROTULO_PAPEL_PARCEIRO[papel].toLowerCase()}.` : null}{' '}
                <button
                  type="button"
                  aria-label="Usar o parceiro existente"
                  className="font-medium text-primary underline"
                  onClick={() => onUsarExistente(existente)}
                >
                  Usar o parceiro existente
                </button>
              </div>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${uid}-nome`}>Nome</Label>
            <Controller
              control={control}
              name="nome"
              render={({ field }) => (
                <Input id={`${uid}-nome`} aria-label="Nome" disabled={travado('nome')} title={tituloDe('nome')} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
              )}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${uid}-fantasia`}>Fantasia</Label>
            <Controller
              control={control}
              name="fantasia"
              render={({ field }) => (
                <Input id={`${uid}-fantasia`} aria-label="Fantasia" disabled={travado('fantasia')} title={tituloDe('fantasia')} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
              )}
            />
          </div>
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-4" disabled={desabilitado}>
        <legend className="text-sm font-semibold text-ink">Fiscal</legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${uid}-ie`}>Inscrição estadual</Label>
            <Controller
              control={control}
              name="ie"
              render={({ field }) => (
                <Input id={`${uid}-ie`} aria-label="Inscrição estadual" disabled={travado('ie')} title={tituloDe('ie')} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
              )}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${uid}-icms`}>Contribuinte de ICMS</Label>
            <Controller
              control={control}
              name="contribuinteIcms"
              render={({ field }) => (
                <select
                  id={`${uid}-icms`}
                  aria-label="Contribuinte de ICMS"
                  disabled={travado('contribuinteIcms')}
                  title={tituloDe('contribuinteIcms')}
                  className={CLASSE_CAMPO}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  ref={field.ref}
                >
                  <option value="">Não informado</option>
                  {CONTRIBUINTES_ICMS.map((c) => (
                    <option key={c} value={c}>
                      {ROTULO_CONTRIBUINTE_ICMS[c]}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-4" disabled={desabilitado}>
        <legend className="text-sm font-semibold text-ink">Observações</legend>
        <Controller
          control={control}
          name="observacoes"
          render={({ field }) => (
            <textarea
              id={`${uid}-observacoes`}
              aria-label="Observações"
              rows={3}
              className="flex w-full min-w-0 rounded-md border border-input bg-card px-3 py-2 text-body text-ink outline-none focus-visible:border-primary focus-visible:shadow-focus"
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          )}
        />
      </fieldset>

      <fieldset className="flex flex-col gap-4" disabled={desabilitado}>
        <legend className="text-sm font-semibold text-ink">Endereços</legend>
        {enderecos.fields.length === 0 ? <p className="text-sm text-ink-muted">Nenhum endereço cadastrado.</p> : null}
        {enderecos.fields.map((item, i) => {
          const status = statusCep[item.id];
          const bloqueado = enderecoTravado(i);
          const id = (c: string): string => `${uid}-end-${item.id}-${c}`;
          return (
            <div key={item.id} className="flex flex-col gap-3 rounded-md border border-input p-4" data-slot="endereco">
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex min-w-40 flex-col gap-1.5">
                  <Label htmlFor={id('tipo')}>Tipo</Label>
                  <Controller
                    control={control}
                    name={`enderecos.${i}.tipo` as const}
                    render={({ field }) => (
                      <select id={id('tipo')} aria-label={`Tipo do endereço ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined} className={CLASSE_CAMPO} value={field.value} onChange={(e) => field.onChange(e.target.value)} onBlur={field.onBlur} ref={field.ref}>
                        {TIPOS_ENDERECO.map((t) => (
                          <option key={t} value={t}>
                            {ROTULO_TIPO_ENDERECO[t]}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>
                <label className="flex h-10 items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name={`${uid}-principal`}
                    aria-label={`Endereço ${i + 1} é o principal`}
                    checked={enderecosAtuais[i]?.principal === true}
                    disabled={principalTravado}
                    title={principalTravado ? TITULO_CAMPO_PROTHEUS : undefined}
                    onChange={() => marcarPrincipal(i)}
                  />
                  Principal
                </label>
                <button type="button" aria-label={`Remover endereço ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined} className="ml-auto h-10 text-sm text-destructive underline" onClick={() => enderecos.remove(i)}>
                  Remover
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={id('cep')}>CEP</Label>
                  <Controller
                    control={control}
                    name={`enderecos.${i}.cep` as const}
                    render={({ field }) => (
                      <Input
                        id={id('cep')}
                        aria-label={`CEP do endereço ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined}
                        inputMode="numeric"
                        placeholder="00000-000"
                        value={mascaraCep(field.value ?? '')}
                        onChange={(e) => field.onChange(soDigitos(e.target.value).slice(0, 8))}
                        onBlur={() => {
                          field.onBlur();
                          void preencherPorCep(i, item.id, field.value ?? '');
                        }}
                        ref={field.ref}
                      />
                    )}
                  />
                  {status === 'buscando' ? <p className="text-sm text-ink-muted">Buscando CEP…</p> : null}
                  {status === 'falhou' ? <p className="text-sm text-ink-muted">Não foi possível buscar o CEP. Preencha manualmente.</p> : null}
                </div>
                <div className="flex flex-col gap-1.5 md:col-span-2">
                  <Label htmlFor={id('logradouro')}>Logradouro</Label>
                  <Controller
                    control={control}
                    name={`enderecos.${i}.logradouro` as const}
                    render={({ field }) => (
                      <Input id={id('logradouro')} aria-label={`Logradouro do endereço ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
                    )}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={id('numero')}>Número</Label>
                  <Controller
                    control={control}
                    name={`enderecos.${i}.numero` as const}
                    render={({ field }) => (
                      <Input id={id('numero')} aria-label={`Número do endereço ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
                    )}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={id('complemento')}>Complemento</Label>
                  <Controller
                    control={control}
                    name={`enderecos.${i}.complemento` as const}
                    render={({ field }) => (
                      <Input id={id('complemento')} aria-label={`Complemento do endereço ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
                    )}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={id('bairro')}>Bairro</Label>
                  <Controller
                    control={control}
                    name={`enderecos.${i}.bairro` as const}
                    render={({ field }) => (
                      <Input id={id('bairro')} aria-label={`Bairro do endereço ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
                    )}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={id('municipio')}>Município</Label>
                  <Controller
                    control={control}
                    name={`enderecos.${i}.municipio` as const}
                    render={({ field }) => (
                      <Input
                        id={id('municipio')}
                        aria-label={`Município do endereço ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined}
                        aria-invalid={erros.enderecos?.[i]?.municipio ? true : undefined}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={() => {
                          field.onBlur();
                          void conferirMunicipio(i);
                        }}
                        ref={field.ref}
                      />
                    )}
                  />
                  {erros.enderecos?.[i]?.municipio?.message ? (
                    <p role="alert" className="text-sm text-destructive">
                      {erros.enderecos[i]?.municipio?.message}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={id('uf')}>UF</Label>
                  <Controller
                    control={control}
                    name={`enderecos.${i}.uf` as const}
                    render={({ field }) => (
                      <select
                        id={id('uf')}
                        aria-label={`UF do endereço ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined}
                        className={CLASSE_CAMPO}
                        value={field.value ?? ''}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          void conferirMunicipio(i);
                        }}
                        onBlur={field.onBlur}
                        ref={field.ref}
                      >
                        <option value="">Selecione</option>
                        {UFS.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>
              </div>
            </div>
          );
        })}
        <div>
          <button
            type="button"
            aria-label="Adicionar endereço"
            disabled={enderecos.fields.length >= LIMITE_ENDERECOS || desabilitado}
            className="h-10 rounded-md border border-input bg-card px-4 text-sm text-ink disabled:opacity-50"
            onClick={() => enderecos.append({ tipo: 'COMERCIAL', principal: enderecos.fields.length === 0, cep: '', logradouro: '', numero: '', complemento: '', bairro: '', municipio: '', uf: '', codigoIbge: '' })}
          >
            Adicionar endereço
          </button>
          <span className="ml-3 text-sm text-ink-muted">
            {enderecos.fields.length} de {LIMITE_ENDERECOS}
          </span>
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-4" disabled={desabilitado}>
        <legend className="text-sm font-semibold text-ink">Contatos</legend>
        {contatos.fields.map((item, i) => {
          const id = (c: string): string => `${uid}-con-${item.id}-${c}`;
          const bloqueado = contatoTravado(i);
          return (
            <div key={item.id} className="flex flex-col gap-3 rounded-md border border-input p-4" data-slot="contato">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={id('nome')}>Nome do contato</Label>
                  <Controller
                    control={control}
                    name={`contatos.${i}.nome` as const}
                    render={({ field }) => (
                      <Input id={id('nome')} aria-label={`Nome do contato ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
                    )}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={id('cargo')}>Cargo</Label>
                  <Controller
                    control={control}
                    name={`contatos.${i}.cargo` as const}
                    render={({ field }) => (
                      <Input id={id('cargo')} aria-label={`Cargo do contato ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
                    )}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={id('email')}>E-mail</Label>
                  <Controller
                    control={control}
                    name={`contatos.${i}.email` as const}
                    render={({ field }) => (
                      <Input id={id('email')} aria-label={`E-mail do contato ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined} type="email" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
                    )}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={id('telefone')}>Telefone</Label>
                  <Controller
                    control={control}
                    name={`contatos.${i}.telefone` as const}
                    render={({ field }) => (
                      <Input
                        id={id('telefone')}
                        aria-label={`Telefone do contato ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined}
                        inputMode="tel"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(mascaraTelefone(e.target.value))}
                        onBlur={field.onBlur}
                        ref={field.ref}
                      />
                    )}
                  />
                </div>
              </div>
              <div>
                <button type="button" aria-label={`Remover contato ${i + 1}`} disabled={bloqueado} title={bloqueado ? TITULO_CAMPO_PROTHEUS : undefined} className="text-sm text-destructive underline" onClick={() => contatos.remove(i)}>
                  Remover contato
                </button>
              </div>
            </div>
          );
        })}
        <div>
          <button
            type="button"
            aria-label="Adicionar contato"
            disabled={contatos.fields.length >= LIMITE_CONTATOS || desabilitado}
            className="h-10 rounded-md border border-input bg-card px-4 text-sm text-ink disabled:opacity-50"
            onClick={() => contatos.append({ nome: '', cargo: '', email: '', telefone: '' })}
          >
            Adicionar contato
          </button>
          <span className="ml-3 text-sm text-ink-muted">
            {contatos.fields.length} de {LIMITE_CONTATOS}
          </span>
        </div>
      </fieldset>
    </div>
  );
}
